import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, SlidersHorizontal, MoreVertical,
  BookOpen, Edit3, Trash2, Layers,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToHostBooks,
  deleteBook,
  Book,
} from "../../redux/bookSlice";

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — same palette as Products.tsx
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg:        "#FBF6EE",
  cardBg:    "#FFFFFF",
  surface:   "#FCEFD4",
  offwhite:  "#FBF6EE",

  dark:      "#1B0E06",
  text:      "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red:       "#C8202E",
  redDark:   "#9E1521",
  redSoft:   "#FBE7E9",

  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",

  border:    "#EEDFC4",
  divider:   "#F3E6CE",

  green:     "#1FAA59",
  greenSoft: "rgba(31,170,89,.10)",
  purple:    "#7A56C9",
  purpleSoft:"rgba(122,86,201,.10)",
  danger:    "#C8202E",
};

const GRAD      = `linear-gradient(90deg, ${HEART.dark} 0%, ${HEART.gold} 35%, ${HEART.red} 65%, ${HEART.redDark} 100%)`;
const GRAD_CTA  = `linear-gradient(135deg, ${HEART.gold} 0%, ${HEART.red} 100%)`;
const GRAD_SIZE = "300% 100%";

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
  *,*::before,*::after { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-thumb { background: ${HEART.border}; border-radius: 8px; }
  input, button { font-family: inherit; }
  body { background: ${HEART.bg}; }

  @keyframes arSpin {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes pulseDot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: .5; transform: scale(1.4); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  input::placeholder { color: ${HEART.textFaint}; }
  input:focus { outline: none; }

  .search-wrap { transition: border-color .15s, box-shadow .15s; }
  .search-wrap:focus-within {
    border-color: rgba(200,32,46,.45) !important;
    box-shadow: 0 0 0 3px rgba(200,32,46,.08);
  }

  .filter-btn { transition: background .15s, border-color .15s; }
  .filter-btn:hover { background: ${HEART.goldSoft} !important; border-color: rgba(200,32,46,.35) !important; }

  .add-btn { transition: transform .15s, box-shadow .15s, filter .15s; }
  .add-btn:hover { transform: translateY(-1px); filter: brightness(1.04); }

  .p-row { transition: box-shadow .2s, transform .15s, border-color .2s; cursor: pointer; }
  .p-row:hover {
    box-shadow: 0 8px 28px rgba(200,32,46,0.10);
    border-color: rgba(242,169,62,.55) !important;
    transform: translateY(-2px);
  }

  .dot-menu {
    position: absolute; right: 12px; top: 52px;
    background: #fff;
    border: 1.5px solid ${HEART.border};
    border-radius: 14px; overflow: hidden; z-index: 50;
    box-shadow: 0 14px 40px rgba(27,14,6,.16); min-width: 168px;
  }
  .dot-menu-item {
    display: flex; align-items: center; gap: 10px; padding: 11px 16px;
    font-size: 13px; font-weight: 600; color: ${HEART.text}; cursor: pointer;
    border: none; background: none; width: 100%; text-align: left;
    transition: background .15s; font-family: 'DM Sans', sans-serif;
  }
  .dot-menu-item:hover { background: ${HEART.goldSoft}; }
  .dot-menu-item.danger { color: ${HEART.danger}; }
  .dot-menu-item.danger:hover { background: ${HEART.redSoft}; }

  .stat-card { transition: border-color .15s, box-shadow .15s; }
  .stat-card:hover { border-color: rgba(200,32,46,.28); box-shadow: 0 4px 16px rgba(27,14,6,.06); }
`;

/* ─── helpers ─── */
const fmtPrice = (n: number) =>
  `€${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ─── BookRow ─── */
interface RowCardProps {
  book:     Book;
  onEdit:   () => void;
  onDelete: () => void;
}

const BookRow: React.FC<RowCardProps> = ({ book, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const isVisual = !!book.isVisualBook;
  const thumb    = !isVisual ? (book.coverPhoto || book.photos?.[0]) : null;
  const pageCount = book.pages?.length ?? 0;

  return (
    <motion.div
      className="p-row"
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      style={{
        position: "relative",
        background: HEART.cardBg,
        border: `1.5px solid ${HEART.border}`,
        borderRadius: 16,
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: 12,
        overflow: "visible",
        boxShadow: "0 2px 12px rgba(27,14,6,0.04)",
      }}
      onClick={onEdit}
    >
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
        borderRadius: "16px 0 0 16px",
        background: GRAD, backgroundSize: GRAD_SIZE, animation: "arSpin 5s linear infinite",
      }} />

      {/* Thumbnail */}
      <div style={{
        width: 88, height: 88, borderRadius: 12, overflow: "hidden",
        background: `linear-gradient(135deg, ${HEART.goldSoft}, #FDF3E1)`,
        flexShrink: 0, position: "relative", marginLeft: 8,
        border: `1px solid ${HEART.border}`,
      }}>
        {thumb ? (
          <img src={thumb} alt={book.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
            <BookOpen size={26} color={HEART.gold} style={{ opacity: 0.65 }} />
            {isVisual && (
              <span style={{ fontSize: 9, fontWeight: 800, color: HEART.goldDark, fontFamily: "'Sora',sans-serif" }}>
                {pageCount} pg
              </span>
            )}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 800, fontSize: 15, color: HEART.text,
          marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis",
          whiteSpace: "nowrap", letterSpacing: -0.2,
          fontFamily: "'Sora', sans-serif",
        }}>
          {book.title || "Untitled Book"}
        </div>

        {book.author && (
          <div style={{
            fontSize: 12, color: HEART.textMuted, marginBottom: 8,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            fontFamily: "'DM Sans', sans-serif",
          }}>
            by {book.author}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {!isVisual ? (
            <span style={{
              display: "inline-flex", alignItems: "center",
              background: GRAD_CTA,
              borderRadius: 8, padding: "4px 12px",
              fontWeight: 900, color: "#fff", fontSize: 13.5, letterSpacing: -0.2,
              fontFamily: "'Sora', sans-serif",
            }}>
              {fmtPrice(book.price ?? 0)}
            </span>
          ) : (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              background: HEART.purpleSoft, color: HEART.purple,
              borderRadius: 8, padding: "4px 12px",
              fontWeight: 800, fontSize: 12.5,
              fontFamily: "'Sora', sans-serif",
              border: "1px solid rgba(122,86,201,.28)",
            }}>
              <Layers size={12} /> {pageCount} {pageCount === 1 ? "page" : "pages"}
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          {book.isPublished ? (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: "0.09em",
              background: HEART.greenSoft, color: HEART.green,
              padding: "2px 8px", borderRadius: 5,
              border: "1px solid rgba(31,170,89,.28)",
              fontFamily: "'Sora',sans-serif",
            }}>LIVE</span>
          ) : (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: "0.09em",
              background: HEART.offwhite, color: HEART.textFaint,
              padding: "2px 8px", borderRadius: 5,
              border: `1px solid ${HEART.border}`,
              fontFamily: "'Sora',sans-serif",
            }}>DRAFT</span>
          )}
          <span style={{
            fontSize: 9, fontWeight: 800, letterSpacing: "0.07em",
            background: isVisual ? HEART.purpleSoft : HEART.goldSoft,
            color: isVisual ? HEART.purple : HEART.goldDark,
            padding: "2px 8px", borderRadius: 5,
            border: `1px solid ${isVisual ? "rgba(122,86,201,.28)" : "rgba(212,144,15,.28)"}`,
            fontFamily: "'Sora',sans-serif",
          }}>
            {isVisual ? "VISUAL BOOK" : `${book.stock ?? 0} IN STOCK`}
          </span>
        </div>
      </div>

      <button
        onClick={e => { e.stopPropagation(); setMenuOpen(v => !v); }}
        style={{
          background: "none", border: "none", cursor: "pointer",
          padding: 6, color: HEART.textFaint, flexShrink: 0,
        }}
      >
        <MoreVertical size={18} />
      </button>

      <AnimatePresence>
        {menuOpen && (
          <>
            <div
              style={{ position: "fixed", inset: 0, zIndex: 49 }}
              onClick={e => { e.stopPropagation(); setMenuOpen(false); }}
            />
            <motion.div
              className="dot-menu"
              initial={{ opacity: 0, scale: 0.92, y: -6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -6 }}
              transition={{ duration: 0.15 }}
              onClick={e => e.stopPropagation()}
            >
              <button className="dot-menu-item" onClick={() => { setMenuOpen(false); onEdit(); }}>
                <Edit3 size={14} color={HEART.red} /> Edit Book
              </button>
              <button className="dot-menu-item danger" onClick={() => { setMenuOpen(false); onDelete(); }}>
                <Trash2 size={14} /> Delete
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN — Books
═══════════════════════════════════════════════════════════ */
const Books: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const authUserId = useSelector((s: RootState) => s.auth.user?.userId);
  const hostId     = userId ?? authUserId ?? "";

  const books = useSelector((s: RootState) => s.books.books);

  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!hostId) return;
    dispatch(listenToHostBooks({ hostId }) as any);
  }, [dispatch, hostId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return books;
    return books.filter((b: any) =>
      `${b.title ?? ""} ${b.author ?? ""} ${b.description ?? ""}`.toLowerCase().includes(q)
    );
  }, [books, search]);

  const handleDelete = (listingId: string) => {
    if (!window.confirm("Delete this book permanently?")) return;
    dispatch(deleteBook(listingId));
  };

  const goToAdd = (listingId?: string) =>
    navigate(listingId ? `/AddBook/${listingId}` : "/AddBook");

  const publishedCount = books.filter((b: any) => b.isPublished).length;
  const draftCount     = books.filter((b: any) => !b.isPublished).length;
  const visualCount    = books.filter((b: any) => b.isVisualBook).length;

  return (
    <div style={{
      minHeight: "100vh",
      background: HEART.bg,
      fontFamily: "'DM Sans', sans-serif",
      paddingBottom: 48,
    }}>
      <style>{CSS}</style>

      <div style={{
        background: HEART.cardBg,
        borderBottom: `1px solid ${HEART.border}`,
        padding: "0 20px 0",
        boxShadow: "0 2px 16px rgba(27,14,6,0.04)",
      }}>
        <div style={{
          height: 3,
          background: GRAD,
          backgroundSize: GRAD_SIZE,
          animation: "arSpin 5s linear infinite",
          marginLeft: -20, marginRight: -20,
        }} />

        <div style={{ paddingTop: 20, paddingBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
            <div>
              <h1 style={{
                fontWeight: 900, fontSize: 26, color: HEART.text,
                letterSpacing: -0.5, lineHeight: 1.1,
                fontFamily: "'Sora', sans-serif",
              }}>
                My Books
              </h1>
              <p style={{ fontSize: 13, color: HEART.textMuted, marginTop: 3 }}>
                Manage physical and visual books
              </p>
            </div>

            <motion.button
              className="add-btn"
              whileTap={{ scale: 0.96 }}
              onClick={() => goToAdd()}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                background: GRAD_CTA,
                border: "none", borderRadius: 12,
                padding: "12px 20px",
                color: "#fff", fontWeight: 700, fontSize: 14,
                cursor: "pointer",
                boxShadow: "0 6px 20px rgba(200,32,46,.28)",
                flexShrink: 0,
                fontFamily: "'Sora', sans-serif",
              }}
            >
              <Plus size={16} /> Add Book
            </motion.button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 18, maxWidth: 620 }}>
            {[
              { label: "Live",   val: publishedCount, col: HEART.green },
              { label: "Drafts", val: draftCount,      col: HEART.textMuted },
              { label: "Visual", val: visualCount,     col: HEART.purple },
              { label: "Total",  val: books.length,    col: HEART.red },
            ].map(s => (
              <div key={s.label} className="stat-card" style={{
                display: "flex", flexDirection: "column", gap: 4,
                background: HEART.offwhite,
                border: `1.5px solid ${HEART.border}`,
                borderRadius: 12, padding: "10px 14px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: s.col, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: HEART.textMuted, fontWeight: 600 }}>{s.label}</span>
                </div>
                <span style={{ fontSize: 20, fontWeight: 900, color: HEART.text, fontFamily: "'Sora',sans-serif", letterSpacing: -0.3 }}>
                  {s.val}
                </span>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
            <div className="search-wrap" style={{
              flex: 1, display: "flex", alignItems: "center", gap: 8,
              background: HEART.offwhite,
              borderRadius: 12,
              border: `1.5px solid ${HEART.border}`,
              padding: "11px 16px",
            }}>
              <Search size={15} color={HEART.textFaint} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search books…"
                style={{
                  flex: 1, background: "none", border: "none", outline: "none",
                  fontSize: 14, color: HEART.text, fontFamily: "'DM Sans',sans-serif",
                }}
              />
            </div>
            <button className="filter-btn" style={{
              display: "flex", alignItems: "center", gap: 7,
              background: HEART.cardBg,
              border: `1.5px solid ${HEART.border}`,
              borderRadius: 12, padding: "11px 18px",
              color: HEART.red, fontWeight: 700, fontSize: 13,
              cursor: "pointer", flexShrink: 0,
              fontFamily: "'DM Sans',sans-serif",
            }}>
              <SlidersHorizontal size={14} color={HEART.red} /> Filter
            </button>
          </div>
        </div>
      </div>

      <div style={{ padding: "16px 16px 0" }}>
        {filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ textAlign: "center", paddingTop: 80 }}
          >
            <div style={{
              width: 78, height: 78, borderRadius: 20, margin: "0 auto 20px",
              background: GRAD_CTA,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 10px 28px rgba(200,32,46,.22)",
            }}>
              <BookOpen size={34} color="#fff" />
            </div>
            <h2 style={{
              fontWeight: 900, fontSize: 20, color: HEART.text, marginBottom: 8,
              fontFamily: "'Sora',sans-serif",
            }}>
              {books.length === 0 ? "No books yet" : "No results found"}
            </h2>
            <p style={{
              color: HEART.textMuted, marginBottom: 28, fontSize: 14,
              lineHeight: 1.7, maxWidth: 280, margin: "0 auto 28px",
            }}>
              {books.length === 0
                ? "Add your first physical or visual book to start selling."
                : "Try adjusting your search or filter."}
            </p>
            {books.length === 0 && (
              <motion.button
                whileTap={{ scale: 0.96 }}
                onClick={() => goToAdd()}
                style={{
                  padding: "13px 32px", borderRadius: 12, border: "none",
                  background: GRAD_CTA,
                  color: "#fff", fontWeight: 700, fontSize: 14,
                  cursor: "pointer", fontFamily: "'Sora',sans-serif",
                  boxShadow: "0 6px 20px rgba(200,32,46,.26)",
                }}
              >
                + Add First Book
              </motion.button>
            )}
          </motion.div>
        ) : (
          <AnimatePresence>
            {filtered.map((book: Book) => (
              <BookRow
                key={book.listingId}
                book={book}
                onEdit={() => goToAdd(book.listingId)}
                onDelete={() => handleDelete(book.listingId)}
              />
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default Books;